import { ref, onMounted, onBeforeUnmount } from 'vue'

const PING_TIMEOUT = 1500

// Asks one extension, by id, whether it is there. Chrome only exposes chrome.runtime to a
// page when an installed extension lists the page in externally_connectable, and answers
// through lastError when nothing is listening. Reading lastError also keeps the console quiet.
function ping(extensionId) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), PING_TIMEOUT)
    try {
      window.chrome.runtime.sendMessage(extensionId, { type: 'ping' }, (response) => {
        clearTimeout(timer)
        if (window.chrome.runtime.lastError || !response?.installed) return resolve(null)
        resolve(response)
      })
    } catch {
      clearTimeout(timer)
      resolve(null)
    }
  })
}

// Whether the Timelydo Jobs extension is installed in this browser.
//   status: 'checking' | 'installed' | 'missing' | 'unsupported'
// 'unsupported' means a browser without the Chrome extension APIs (Firefox, Safari), where
// this cannot be detected. 'missing' covers not installed, disabled, and another profile.
export function useExtensionDetection() {
  const status = ref('checking')
  const version = ref(null)
  const connected = ref(false)

  async function check() {
    if (!window.chrome) {
      status.value = 'unsupported'
      return
    }

    if (window.chrome.runtime?.sendMessage) {
      for (const id of window.EXTENSION_IDS || []) {
        const response = await ping(id)
        if (response) {
          status.value = 'installed'
          version.value = response.version || null
          connected.value = Boolean(response.connected)
          return
        }
      }
    }

    status.value = 'missing'
    version.value = null
    connected.value = false
  }

  // The user installs the extension in another tab, then comes back here.
  onMounted(() => {
    check()
    window.addEventListener('focus', check)
  })
  onBeforeUnmount(() => window.removeEventListener('focus', check))

  return { status, version, connected, check }
}
